import BaseModel from './BaseModel';
import MultiLanguageDesignationModel from './MultiLanguageDesignationModel';
import * as AT from '../../components/container/attributes';
import MultiLanguageField from './MultiLanguageField';
import {HitOnlineEnums} from 'hit-online-web-ui/src/helpers/constants';
import {parseLocale} from '../../utils/language.js';
import {translate} from '../../plugins/i18n/i18n';

const max3Characters = (data) => {
  if (data) {
    return data.length <= 3;
  } else {
    return true;
  }
};

export default class Unit extends MultiLanguageDesignationModel {
  constructor({
    id,
    number,
    designationFrBe = undefined,
    designationDeBe = undefined,
    designationNlBe = undefined,
    designationFrLu = undefined,
    designationDeLu = undefined,
    designationFrFr = undefined,
    designationDeDe = undefined,
    designationNlNl = undefined,
    designationEnGb = undefined,
    designationEnIn = undefined,
    designationMrIn = undefined,
    designationHiIn = undefined,
    unitFrBe = undefined,
    unitDeBe = undefined,
    unitNlBe = undefined,
    unitFrLu = undefined,
    unitDeLu = undefined,
    unitFrFr = undefined,
    unitDeDe = undefined,
    unitNlNl = undefined,
    unitEnGb = undefined,
    unitEnIn = undefined,
    unitMrIn = undefined,
    unitHiIn = undefined,
    category = undefined,
    factor = undefined,
    peppolId = undefined,

    // We also need to add the key how it is stored in the db (we use the db column names)
    no = undefined,
    designation = undefined,
    unit = undefined,
  }) {
    super({
      id,
      number: number || no,
      designationFrBe,
      designationDeBe,
      designationNlBe,
      designationFrLu,
      designationDeLu,
      designationFrFr,
      designationDeDe,
      designationNlNl,
      designationEnGb,
      designationEnIn,
      designationMrIn,
      designationHiIn,
    });
    if (designation) {
      this.designation = designation;
    }
    this.units = new MultiLanguageField({
      unitFrBe,
      unitDeBe,
      unitNlBe,
      unitFrLu,
      unitDeLu,
      unitFrFr,
      unitDeDe,
      unitNlNl,
      unitEnGb,
      unitEnIn,
      unitMrIn,
      unitHiIn,
    });
    this.unit = unit || this.units.value;
    this.category = category
      ? HitOnlineEnums.getEnumValue('UNIT_CATEGORY', category, translate)
      : null;
    this.factor = factor;
    this.peppolId = peppolId;
  }

  unitForLocale(locale) {
    if (!locale || !this.units?.values) return this.unit;
    try {
      const localized = this.units.values[parseLocale(locale).idApp];
      return localized || this.unit;
    } catch {
      return this.unit;
    }
  }

  get fullDesignation() {
    return this.unitForLocale(this.displayLocale);
  }

  get fastModeBrowserDesignation() {
    return this.unitForLocale(this.displayLocale);
  }

  static get fkName() {
    return 'unit_id';
  }
  static get apiRoute() {
    return 'unit';
  }
  static get listPageRoute() {
    return 'unitList';
  }
  static get detailPageRoute() {
    return 'unitList';
  }
  static get createInitColumns() {
    return {};
  }
  static get config() {
    const config = BaseModel.config;
    config['addable'] = false;
    return config;
  }
  get fkName() {
    return Unit.fkName;
  }
  get apiRoute() {
    return Unit.apiRoute;
  }
  get detailPageRoute() {
    return Unit.detailPageRoute;
  }
  get listPageRoute() {
    return Unit.listPageRoute;
  }
  get config() {
    return Unit.config;
  }

  static get browserDisplayAttrs() {
    return this.getAttributes(
      [
        'number',
        ...this.localeAttributeNames('designation'),
        ...this.localeAttributeNames('unit'),
      ],
      ['category', 'peppolId'],
    );
  }

  static get joinAttrsKey() {
    return [
      'number',
      ...this.localeAttributeNames('designation'),
      ...this.localeAttributeNames('unit'),
    ];
  }

  static get attrsToStoreInForm() {
    return ['number', 'designation', 'unit'];
  }
  get attrsToStoreInForm() {
    return Unit.attrsToStoreInForm;
  }

  static get listDisplayAttrs() {
    return this.getAttributes([
      'number',
      this.attributeUserLocale('designation'),
      this.attributeCompanyLocale('designation'),
      this.attributeUserLocale('unit'),
      this.attributeCompanyLocale('unit'),
      'category',
      'factor',
      'peppolId',
    ]);
  }

  static get allAttributes() {
    return [
      this.NUMBER_ATTRIBUTE(),
      new AT.EnumerationAttribute({
        name: 'category',
        enumType: 'UNIT_CATEGORY',
        enumClass: HitOnlineEnums,
        column: 'category',
        tableProperties: {
          header: 'hit-app.common.category',
          maxWidth: '1fr',
        },
      }),
      new AT.DecimalAttribute({
        name: 'factor',
        decimalPrecision: 4,
        creationValue: 1,
        column: 'factor',
        tableProperties: {
          header: 'hit-app.common.factor',
          maxWidth: '1fr',
        },
      }),
      new AT.StringAttribute({
        name: 'peppolId',
        validations: {max3Characters},
        column: 'peppol_id',
        tableProperties: {
          header: 'hit-app.common.peppol-id',
          maxWidth: '0.7fr',
        },
      }),
      this.ACTIVE_ATTRIBUTE(),
    ]
      .concat(
        this.generateMultiLanguageField({
          key: 'designation',
          attributeClass: AT.StringAttribute,
          header: 'hit-base.common.designation',
        }),
      )
      .concat(
        this.generateMultiLanguageField({
          key: 'unit',
          attributeClass: AT.StringAttribute,
          header: 'hit-base.common.unit',
        }),
      );
  }

  static async unitsMap() {
    const units = await this.loadCustomEntity(null, {});
    const unitsMap = {};
    if (units.length > 0) {
      units.forEach((unit) => {
        unitsMap[unit.id] = unit;
      });
      return unitsMap;
    }
    return {};
  }
}
